"use client";
import {
  ONBOARDING_FORM_STYLES,
  ONBOARDING_SELECT_CLASS_NAMES,
} from "@/features/onboarding/components/onboarding-form-styles";
import React, { useEffect, useState, useId, type ReactNode } from "react";

import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select } from "@/components/Selects/select";
import Loading from "@/components/Loading";
import { AlertCircle, Check, HelpCircle } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { CityOption, CitySelect } from "@/components/Selects/CitySelect";
import { EmploymentTypeSelect } from "@/components/Selects/EmploymentTypeSelect";
import { PracticeSettingSelect } from "@/components/Selects/PracticeSettingSelect";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { PROFILE_SELECT_CLASS_NAMES } from "@/components/Selects/profile-select-styles";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTRPC } from "@/trpc";
import {
  EmploymentType,
  PracticeSetting,
  JobSearchStatus,
} from "@api/shared-runtime/enums";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Inputs = {
  practiceSettingIds: PracticeSetting[];
  employmentTypeIds: EmploymentType[];
  preferredCities: CityOption[];
  jobSearchStatusId: JobSearchStatus;
  minHourlyRate: number | null;
  openToOtherCities: boolean | null;
  availabilityDescription: string;
  includeExternalJobs: boolean;
};

const jobSearchStatus = [
  { value: JobSearchStatus.ActivelyLooking, label: "Actively Looking" },
  { value: JobSearchStatus.PassivelyLooking, label: "Passively Looking" },
  { value: JobSearchStatus.NotLooking, label: "Not Looking" },
];

const otherCitiesOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

function JobPreferenceSection({
  appearance,
  title,
  children,
}: {
  appearance: "default" | "profile" | "onboarding";
  title: string;
  children: ReactNode;
}) {
  if (appearance === "default") {
    return <>{children}</>;
  }
  const styles =
    appearance === "onboarding" ? ONBOARDING_FORM_STYLES : PROFILE_FORM_STYLES;

  return (
    <section className={cn(styles.section, "space-y-5")}>
      <h3 className={styles.sectionHeading}>{title}</h3>
      {children}
    </section>
  );
}

export default function JobPreferencesForm({
  userId,
  onComplete,
  onCancel,
  appearance = "default",
}: {
  userId?: string;
  onComplete?: () => void;
  onCancel?: () => void;
  appearance?: "default" | "profile" | "onboarding";
}) {
  const formId = useId();
  const isProfile = appearance === "profile";
  const isOnboarding = appearance === "onboarding";
  const isStyled = appearance !== "default";
  const styles = isOnboarding ? ONBOARDING_FORM_STYLES : PROFILE_FORM_STYLES;
  const labelClassName = isStyled ? styles.label : undefined;
  let selectClassNames: typeof PROFILE_SELECT_CLASS_NAMES | undefined;
  if (isOnboarding) {
    selectClassNames = ONBOARDING_SELECT_CLASS_NAMES;
  } else if (isProfile) {
    selectClassNames = PROFILE_SELECT_CLASS_NAMES;
  }
  const selectMenuPlacement = isStyled ? "auto" : undefined;
  const controlClassName = isOnboarding
    ? ONBOARDING_FORM_STYLES.input
    : PROFILE_FORM_STYLES.control;
  let bodyClassName;
  if (isOnboarding) {
    bodyClassName = ONBOARDING_FORM_STYLES.body;
  } else if (isProfile) {
    bodyClassName = PROFILE_FORM_STYLES.body;
  }
  const trpc = useTRPC();
  const [justSaved, setJustSaved] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConsultingDialog, setShowConsultingDialog] =
    useState<boolean>(false);
  const [pendingData, setPendingData] = useState<Inputs | null>(null);

  const { data: result, isLoading } = useQuery(
    trpc.profile.getJobPreferences.queryOptions({
      userId,
    }),
  );

  const { mutateAsync: saveJobPreferences, isPending } = useMutation({
    ...trpc.profile.updateJobPreferences.mutationOptions(),
    onSuccess: async () => {
      setSubmitError(null);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
      await queryClient.invalidateQueries(
        trpc.profile.getJobPreferences.queryOptions({
          userId,
        }),
      );
      onComplete?.();
    },
    onError: (error) => {
      setSubmitError(
        error.message || "Failed to save job preferences. Please try again.",
      );
    },
  });

  useEffect(() => {
    const data = result;
    if (data) {
      let openToOtherCities: boolean | null = null;
      if (data.searchStatusId !== JobSearchStatus.NotLooking) {
        openToOtherCities = data.cities.some(
          (city) => city.name === "ANY_CITY",
        );
      }

      const filteredCities = data.cities.filter(
        (city) => city.name !== "ANY_CITY",
      );

      form.reset({
        preferredCities: filteredCities,
        practiceSettingIds: data.practiceSettingIds ?? [],
        employmentTypeIds: data.employmentTypeIds ?? [],
        jobSearchStatusId: data.searchStatusId,
        minHourlyRate: data.minHourlyRate ? Number(data.minHourlyRate) : null,
        openToOtherCities,
        availabilityDescription: data.availabilityDescription || "",
        includeExternalJobs: data.includeExternalJobs ?? true,
      });
    }
  }, [result]);

  const form = useForm<Inputs>({
    defaultValues: {
      practiceSettingIds: [],
      employmentTypeIds: [],
      preferredCities: [],
      jobSearchStatusId: JobSearchStatus.ActivelyLooking,
      minHourlyRate: null,
      openToOtherCities: null,
      availabilityDescription: "",
      includeExternalJobs: true,
    },
    mode: "onSubmit",
  });

  const lookingForWork =
    form.watch("jobSearchStatusId") !== JobSearchStatus.NotLooking;

  const performSave = async (data: Inputs) => {
    setSubmitError(null);

    const cities = [...data.preferredCities];
    if (data.openToOtherCities) {
      cities.push({ name: "ANY_CITY", lat: 0, lng: 0 });
    }

    await saveJobPreferences({
      practiceSettingIds: data.practiceSettingIds,
      employmentTypeIds: data.employmentTypeIds,
      preferredCities: cities,
      searchStatusId: data.jobSearchStatusId,
      minHourlyRate: data.minHourlyRate,
      availabilityDescription:
        data.availabilityDescription?.trim().length === 0
          ? null
          : data.availabilityDescription,
      includeExternalJobs: data.includeExternalJobs,
    });
  };

  const onSubmit = async (data: Inputs) => {
    // Check if user is looking for work, has selected at least one employment type,
    // but hasn't selected consulting
    const hasEmploymentTypes = data.employmentTypeIds.length > 0;
    const hasConsulting = data.employmentTypeIds.includes(
      EmploymentType.Consulting,
    );

    if (lookingForWork && hasEmploymentTypes && !hasConsulting) {
      // Show dialog asking if they want to add consulting
      setPendingData(data);
      setShowConsultingDialog(true);
      return;
    }

    // Otherwise, save directly
    await performSave(data);
  };

  const handleAddConsulting = async () => {
    if (!pendingData) {
      return;
    }

    // Add consulting to the employment types
    const updatedData = {
      ...pendingData,
      employmentTypeIds: [
        ...pendingData.employmentTypeIds,
        EmploymentType.Consulting,
      ],
    };

    setShowConsultingDialog(false);
    setPendingData(null);
    await performSave(updatedData);
  };

  const handleSkipConsulting = async () => {
    if (!pendingData) {
      return;
    }

    setShowConsultingDialog(false);
    await performSave(pendingData);
    setPendingData(null);
  };

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex justify-center items-center w-full",
          isStyled && "min-h-48 flex-1 py-12",
        )}
      >
        <Loading className={isStyled ? "size-10 text-primary" : "w-20 h-20"} />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        className={cn(
          "w-full",
          isProfile && "flex min-h-0 flex-1 flex-col overflow-hidden",
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className={bodyClassName}>
          <JobPreferenceSection appearance={appearance} title="Your search">
            <FormField
              control={form.control}
              name="jobSearchStatusId"
              rules={{
                required: "Job search status is required",
              }}
              render={({ field }) => (
                <FormItem className={isStyled ? "space-y-2" : "mb-4.5"}>
                  <FormLabel
                    htmlFor={`${formId}-${field.name}`}
                    className={labelClassName}
                  >
                    Job Search Status
                  </FormLabel>
                  <FormControl>
                    <Select
                      inputId={`${formId}-${field.name}`}
                      ref={field.ref}
                      placeholder="Select job search status"
                      options={jobSearchStatus}
                      classNames={selectClassNames}
                      menuPlacement={selectMenuPlacement}
                      value={jobSearchStatus.find(
                        (option) => option.value === field.value,
                      )}
                      onChange={(
                        selectedOption: {
                          value: number | string;
                          label: string | number;
                        } | null,
                      ) => {
                        field.onChange(
                          selectedOption
                            ? Number(selectedOption.value)
                            : JobSearchStatus.ActivelyLooking,
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </JobPreferenceSection>

          {lookingForWork && (
            <>
              <JobPreferenceSection
                appearance={appearance}
                title="Role & compensation"
              >
                <FormField
                  control={form.control}
                  name="minHourlyRate"
                  rules={{
                    min: {
                      value: 0,
                      message: "Rate cannot be negative",
                    },
                    pattern: {
                      value: /^\d*\.?\d{0,2}$/,
                      message: "Hourly rate must be a decimal number",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem className={isStyled ? "space-y-2" : "mb-4.5"}>
                      <FormLabel
                        htmlFor={`${formId}-${field.name}`}
                        className={labelClassName}
                      >
                        Minimum Hourly Rate (USD)
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span
                            className={cn(
                              "absolute left-3 top-1/2 transform -translate-y-1/2",
                              isProfile && "text-sm font-medium text-slate-400",
                            )}
                          >
                            $
                          </span>
                          <Input
                            id={`${formId}-${field.name}`}
                            type="text"
                            inputMode="decimal"
                            className={cn(isStyled && controlClassName, "pl-7")}
                            placeholder="0.00"
                            {...field}
                            value={field.value === null ? "" : field.value}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                field.onChange(null);
                              } else {
                                const parsedValue = Number(value);
                                if (Number.isNaN(parsedValue)) {
                                  return;
                                }
                                field.onChange(parsedValue);
                              }
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="practiceSettingIds"
                  render={({ field }) => (
                    <FormItem className={isStyled ? "space-y-2" : "mb-4.5"}>
                      <FormLabel
                        htmlFor={`${formId}-${field.name}`}
                        className={labelClassName}
                      >
                        Preferred Practice Settings
                      </FormLabel>
                      <FormControl>
                        <PracticeSettingSelect
                          inputId={`${formId}-${field.name}`}
                          value={field.value}
                          onChange={field.onChange}
                          classNames={selectClassNames}
                          menuPlacement={selectMenuPlacement}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="employmentTypeIds"
                  render={({ field }) => (
                    <FormItem className={isStyled ? "space-y-2" : "mb-4.5"}>
                      <FormLabel
                        htmlFor={`${formId}-${field.name}`}
                        className={labelClassName}
                      >
                        Employment Type Preferences
                      </FormLabel>
                      <FormControl>
                        <EmploymentTypeSelect
                          inputId={`${formId}-${field.name}`}
                          value={field.value}
                          onChange={field.onChange}
                          classNames={selectClassNames}
                          menuPlacement={selectMenuPlacement}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </JobPreferenceSection>

              <JobPreferenceSection
                appearance={appearance}
                title="Schedule & availability"
              >
                <FormField
                  control={form.control}
                  name="availabilityDescription"
                  rules={{
                    required: lookingForWork
                      ? "Availability description is required"
                      : false,
                  }}
                  render={({ field }) => (
                    <FormItem className={isStyled ? "space-y-2" : "mb-4.5"}>
                      <FormLabel className={labelClassName}>
                        Describe your availability and ideal job (Days, time,
                        what you are doing, etc.)
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Describe your availability and ideal job. (Days, time, what you are doing, etc.)"
                          className={isStyled ? styles.textarea : "mt-1"}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </JobPreferenceSection>

              <JobPreferenceSection
                appearance={appearance}
                title="Locations & travel"
              >
                <FormField
                  control={form.control}
                  name="preferredCities"
                  render={({ field }) => (
                    <FormItem className={isStyled ? "space-y-2" : "mb-4"}>
                      <FormLabel
                        htmlFor={`${formId}-${field.name}`}
                        className={labelClassName}
                      >
                        Preferred Cities
                      </FormLabel>
                      <FormControl>
                        <CitySelect
                          inputId={`${formId}-${field.name}`}
                          value={field.value}
                          onChange={field.onChange}
                          multiple={true}
                          classNames={selectClassNames}
                          menuPlacement={selectMenuPlacement}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="openToOtherCities"
                  rules={{
                    validate: (value) =>
                      !lookingForWork ||
                      value !== null ||
                      "Please select whether you are open to traveling to other states",
                  }}
                  render={({ field }) => (
                    <FormItem className={isStyled ? "space-y-2" : "mb-4"}>
                      <FormLabel
                        htmlFor={`${formId}-${field.name}`}
                        className={labelClassName}
                      >
                        Are you open to traveling to other states?
                      </FormLabel>
                      <FormControl>
                        <Select
                          inputId={`${formId}-${field.name}`}
                          placeholder="Select an option"
                          options={otherCitiesOptions}
                          value={otherCitiesOptions.find(
                            (option) => option.value === field.value,
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          classNames={selectClassNames}
                          menuPlacement={selectMenuPlacement}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </JobPreferenceSection>

              <JobPreferenceSection
                appearance={appearance}
                title="Job discovery"
              >
                <FormField
                  control={form.control}
                  name="includeExternalJobs"
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        "flex items-center gap-3 space-y-0",
                        !isStyled && "mb-4",
                      )}
                    >
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className={
                            isStyled
                              ? "data-[state=checked]:bg-primary data-[state=unchecked]:bg-slate-200 focus-visible:ring-ring"
                              : undefined
                          }
                        />
                      </FormControl>
                      <div className="flex items-center gap-1">
                        <FormLabel
                          className={cn(
                            "cursor-pointer",
                            isStyled ? labelClassName : "font-normal",
                          )}
                        >
                          Include External Jobs
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger
                            type="button"
                            aria-label="About external jobs"
                            className={
                              isStyled
                                ? "rounded-lg p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                : undefined
                            }
                          >
                            <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                          </PopoverTrigger>
                          <PopoverContent
                            className={cn(
                              "max-w-xs text-sm",
                              isProfile &&
                                "rounded-2xl border-slate-200 p-4 leading-relaxed text-slate-500 shadow-lg",
                            )}
                          >
                            <p>
                              External jobs are positions sourced from
                              third-party job boards and aggregators. Disable
                              this to only see jobs posted directly on Saile.
                            </p>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </JobPreferenceSection>
            </>
          )}

          {submitError && (
            <div
              className={
                isStyled
                  ? styles.error
                  : "mb-4 p-4 rounded-lg border bg-red-50 border-red-200"
              }
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
                <p className="text-red-800">{submitError}</p>
              </div>
            </div>
          )}
        </div>

        <div className={isStyled ? styles.footer : "mb-5"}>
          <Button
            size="none"
            className={
              isStyled
                ? cn(styles.primaryButton, "sm:order-2")
                : "w-full text-lg py-4"
            }
            type="submit"
            disabled={justSaved || form.formState.isSubmitting}
          >
            {form.formState.isSubmitting || isPending ? (
              <Loading className="w-6 h-6" />
            ) : justSaved ? (
              <div className="flex gap-2 items-center animate-fade-in">
                <Check className="w-6 h-6" /> Saved!
              </div>
            ) : (
              "Save"
            )}
          </Button>
          {onCancel && (
            <Button
              size="none"
              variant="outline"
              className={
                isStyled
                  ? cn(styles.secondaryButton, "sm:order-1")
                  : "w-full text-lg py-4 mt-3"
              }
              type="button"
              onClick={onCancel}
              disabled={form.formState.isSubmitting || isPending}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>

      <AlertDialog
        open={showConsultingDialog}
        onOpenChange={(open) => {
          // Only allow closing via our explicit handlers, not backdrop click
          if (!open && pendingData) {
            // User tried to close without choosing - treat as skip
            handleSkipConsulting();
          }
        }}
      >
        <AlertDialogContent
          className={isStyled ? styles.confirmation : undefined}
        >
          <AlertDialogHeader
            className={isStyled ? "space-y-3 text-left" : undefined}
          >
            <AlertDialogTitle
              className={isStyled ? PROFILE_FORM_STYLES.title : undefined}
            >
              Add consulting as an employment type?
            </AlertDialogTitle>
            <AlertDialogDescription
              className={isStyled ? styles.description : undefined}
            >
              Most providers like consulting. Are you sure you don't want to add
              it to your preferences?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter
            className={isStyled ? "mt-6 gap-3 sm:space-x-0" : undefined}
          >
            <Button
              variant="outline"
              onClick={handleSkipConsulting}
              disabled={isPending}
              className={isStyled ? styles.secondaryButton : undefined}
            >
              {isPending ? <Loading className="w-4 h-4" /> : "No thanks"}
            </Button>
            <Button
              onClick={handleAddConsulting}
              disabled={isPending}
              className={isStyled ? styles.primaryButton : undefined}
            >
              {isPending ? (
                <Loading className="w-4 h-4" />
              ) : (
                "Yes, add consulting"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
}
